import { Close, Download, ZoomIn, ZoomOut } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, CircularProgress, Dialog, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { track, triggerDownload as saveFromUrl, useFileDownload, useFilePreview } from '../../../logic';
import { DownloadLinkType } from '../../../types';
import { PreviewKind } from '../../../utils';

const PdfViewer = lazy(() => import('./PdfViewer'))

const MIN_ZOOM = 0.5
const MAX_ZOOM = 3
const ZOOM_STEP = 0.25

export type PreviewFile = {
  FileName: string;
  CompanyIdOrderIdHash: string;
  Type: DownloadLinkType;
  DocumentBatchId?: string;
  DocumentFileName?: string;
}

// How long a saved copy's object URL stays valid; long enough for the browser to start saving it.
const SAVE_URL_LIFETIME_MS = 60_000

/**
 * Shows a PDF or image inside the portal, with a button to download it.
 * The file is fetched each time the dialog opens, since its signed link is short-lived.
 * Downloading from the dialog saves the copy already loaded, so the file is
 * only requested (and logged) once.
 */
export function DocumentPreviewDialog({ open, onClose, title, kind, file }: {
  open: boolean;
  onClose: () => void;
  /** Shown in the header; usually the file's display name. */
  title: string;
  kind: PreviewKind;
  file: PreviewFile;
}) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const preview = useFilePreview({ enabled: open, ...file })
  const { triggerDownload, isDownloading, setIsDownloading } = useFileDownload(file)

  const [zoom, setZoom] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [viewerFailed, setViewerFailed] = useState(false)

  useEffect(() => {
    if (!open) return
    setZoom(1)
    setPageCount(0)
    setCurrentPage(1)
    setViewerFailed(false)
  }, [open])

  const onViewerError = useCallback(() => setViewerFailed(true), [])

  const onDownload = () => {
    if (preview.status === 'ready') {
      track(`Download ${file.Type} from preview`, file.FileName)
      const url = URL.createObjectURL(preview.blob)
      saveFromUrl(url, file.FileName)
      setTimeout(() => URL.revokeObjectURL(url), SAVE_URL_LIFETIME_MS)
      return
    }
    // Nothing loaded to save (the preview failed), so fall back to a regular download.
    setIsDownloading(true)
    triggerDownload()
  }

  const showError = preview.status === 'error' || viewerFailed
  const showPdfControls = kind === 'pdf' && preview.status === 'ready' && !viewerFailed && pageCount > 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      aria-labelledby="document-preview-title"
      PaperProps={{ sx: { height: fullScreen ? '100%' : '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography id="document-preview-title" variant="h6" component="h2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>

        {showPdfControls && (
          <>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }} aria-live="polite">
              Page {currentPage} of {pageCount}
            </Typography>
            <Tooltip title="Zoom out">
              <span>
                <IconButton
                  aria-label="Zoom out"
                  disabled={zoom <= MIN_ZOOM}
                  onClick={() => setZoom(value => Math.max(MIN_ZOOM, value - ZOOM_STEP))}
                >
                  <ZoomOut />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom in">
              <span>
                <IconButton
                  aria-label="Zoom in"
                  disabled={zoom >= MAX_ZOOM}
                  onClick={() => setZoom(value => Math.min(MAX_ZOOM, value + ZOOM_STEP))}
                >
                  <ZoomIn />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        <Tooltip title="Download">
          <span>
            <IconButton aria-label="Download" onClick={onDownload} disabled={isDownloading || preview.status === 'loading'}>
              {isDownloading ? <CircularProgress size={24} /> : <Download />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton aria-label="Close preview" onClick={onClose} edge="end">
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {showError ? (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Alert severity="warning" sx={{ width: '100%', maxWidth: 600 }}>
              We couldn't show a preview of this file. You can still download it.
            </Alert>
            <LoadingButton variant="contained" endIcon={<Download />} loading={isDownloading} onClick={onDownload}>
              Download
            </LoadingButton>
          </Box>
        ) : preview.status === 'loading' ? (
          <Loading />
        ) : kind === 'pdf' ? (
          <Suspense fallback={<Loading />}>
            <PdfViewer
              file={preview.blob}
              zoom={zoom}
              onPageCountChange={setPageCount}
              onCurrentPageChange={setCurrentPage}
              onError={onViewerError}
            />
          </Suspense>
        ) : (
          <ImageViewer blob={preview.blob} alt={title} onError={onViewerError} />
        )}
      </Box>
    </Dialog>
  )
}

function ImageViewer({ blob, alt, onError }: { blob: Blob; alt: string; onError: () => void }) {
  const [src, setSrc] = useState<string>()

  useEffect(() => {
    const url = URL.createObjectURL(blob)
    setSrc(url)
    return () => URL.revokeObjectURL(url)
  }, [blob])

  return (
    <Box sx={{ height: '100%', overflow: 'auto', bgcolor: 'grey.200', display: 'flex', p: 2 }}>
      {src && (
        <Box
          component="img"
          src={src}
          alt={alt}
          onError={onError}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', m: 'auto', bgcolor: 'common.white', boxShadow: 2 }}
        />
      )}
    </Box>
  )
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
      <CircularProgress aria-label="Loading preview" />
    </Box>
  )
}
